import { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { login, isLogged } from '@utils/auth';
import { SIGNIN_URL } from '@data/constants';

interface AuthContextType {
  isAuthenticated: boolean;
  loginWithCode: (code: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isAuthenticated, setAuthenticated] = useState<boolean>(isLogged());

  useEffect(() => {
    if (isAuthenticated) {
      // Optionally, you can implement logic here for when authentication state changes
      // redirecting to a different page, etc.
    }
  }, [isAuthenticated]);

  const loginWithCode = async (code: string) => {
    const response = await login({ cognitoCode: code });
    Cookies.set('access_token', response.accessToken);
    Cookies.set('refresh_token', response.refreshToken);
    setAuthenticated(true);
  };

  const logout = () => {
    Cookies.remove('access_token');
    Cookies.remove('refresh_token');
    setAuthenticated(false);

    const url = new URL(window.location.href);
    url.searchParams.delete('code');
    window.location.href = `${SIGNIN_URL}?redirect_url=${url.toString()}`;
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, loginWithCode, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error(
      'useAuthContext must be used within an AuthContextProvider'
    );
  }
  return context;
}
